"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Quote, MapPin } from "lucide-react";
const testimonials = [{
  quote: "The sunrise over the rice terraces in Bali was a moment of pure stillness. Innoleaf curated an experience that felt like a private dream.",
  attribution: "A.K. \u00B7 BALI ESCAPE",
  location: "UBUD, MAY",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/bali_testimonial.png"
}, {
  quote: "Solo traveling to Manali with Innoleaf was transformative. Everything was handled with such precision, I only had to focus on the mountains.",
  attribution: "S.V. \u00B7 SOLOIST EXPEDITION",
  location: "MANALI, OCTOBER",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713427/img/manali_testimonial.png"
}];
function TestimonialCard({
  quote,
  attribution,
  location,
  image,
  delay,
  index
}: {
  quote: string;
  attribution: string;
  location: string;
  image: string;
  delay: number;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isEven = index % 2 === 0;
  return <div data-index={index} ref={ref} style={{
    animationDelay: `${delay}ms`,
    opacity: isVisible ? 1 : 0
  }} className={`group relative mb-16 md:mb-24 last:mb-0 transition-all duration-1000 border border-accent/20 p-6 md:p-10 rounded-lg ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
      <div className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 md:gap-10 items-center`}>
        {}
        <div className="w-full md:w-1/2 relative">
          <div className="aspect-[4/5] overflow-hidden rounded-lg shadow-2xl relative z-10">
            <img src={image} data-wvc-srcset={`${image}-wvc-srcset`} data-wvc-sizes={`${image}-wvc-sizes`} alt={attribution} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
            <div className="absolute inset-0 bg-primary/5 mix-blend-multiply" />
          </div>
          {}
          <div className={`absolute -bottom-6 ${isEven ? '-right-6' : '-left-6'} w-2/3 h-2/3 bg-secondary/10 rounded-lg -z-0`} />
        </div>

        {}
        <div className="w-full md:w-1/2 flex flex-col justify-center">
          <div className="relative">
            <Quote className="text-secondary/20 w-12 h-12 mb-6" strokeWidth={1} />
            
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-3 h-3 text-primary" />
              <span className="text-[0.7rem] tracking-[0.25em] uppercase font-sans text-primary font-bold">
                {location}
              </span>
            </div>

            <blockquote className="font-serif text-2xl md:text-4xl leading-tight text-foreground italic font-light mb-8">
              &ldquo;{quote}&rdquo;
            </blockquote>

            <div className="flex items-center gap-4">
              <div className="h-px w-12 bg-secondary" />
              <cite className="not-italic font-sans text-[0.8rem] tracking-widest text-muted-foreground uppercase font-medium"></cite>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function Chronicles() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="17849" id="voyager-chronicles" className="bg-background text-foreground py-12 lg:py-18 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headerRef} className={`max-w-4xl mb-24 md:mb-32 transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="text-primary font-sans text-xs md:text-sm font-bold tracking-[0.4em] uppercase mb-8">
            Traveler Chronicles
          </h3>
          <h2 className="font-serif text-5xl md:text-7xl font-light leading-[1.05] tracking-tight mb-10">
            Stories from our <em className="italic text-secondary">travelers&rsquo;</em> personal journeys.
          </h2>
          <h3 className="sr-only">Testimonials</h3>
          <p className="text-muted-foreground font-serif text-xl md:text-2xl max-w-2xl leading-relaxed">
            Real experiences from the Innoleaf community, capturing the essence of modern luxury and transformative exploration.
          </p>
        </div>

        <div className="relative">
          {testimonials.map((t, i) => <TestimonialCard key={i} index={i} quote={t.quote} attribution={t.attribution} location={t.location} image={t.image} delay={i * 200} />)}
        </div>
      </div>

      {}
      <div className="mt-24 md:mt-40 px-6 md:px-12">
        <div className="max-w-7xl mx-auto h-px w-full bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
    </section>;
}